
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertCircle, ExternalLink, RefreshCw } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { useMediaQuery } from "@/hooks/use-media-query";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

interface UmamiConfig {
  scriptUrl: string;
  websiteId: string;
  isConfigured: boolean;
  directShareUrl?: string;
}

const UmamiAnalytics: React.FC = () => {
  const { toast } = useToast();
  const [config, setConfig] = useState<UmamiConfig>(() => {
    // Try to load from localStorage
    try {
      const saved = localStorage.getItem('umamiConfig');
      return saved ? JSON.parse(saved) : { 
        scriptUrl: '', 
        websiteId: '',
        isConfigured: false,
        directShareUrl: 'https://us.umami.is/share/KmVfwgxYdaTMI9xa/founder.bitroot.org'
      };
    } catch (error) {
      console.error('Error loading Umami config:', error);
      return { 
        scriptUrl: '', 
        websiteId: '', 
        isConfigured: false,
        directShareUrl: 'https://us.umami.is/share/KmVfwgxYdaTMI9xa/founder.bitroot.org'
      };
    }
  });
  const [isEditing, setIsEditing] = useState(!config.isConfigured);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [openEmbeddedDashboard, setOpenEmbeddedDashboard] = useState(false);
  const [iframeError, setIframeError] = useState(false);
  const isDesktop = useMediaQuery("(min-width: 768px)");

  const handleSave = () => {
    if (!config.scriptUrl || !config.websiteId) {
      toast({
        title: "Missing information",
        description: "Please provide both the script URL and website ID",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    
    // Short delay to simulate processing and ensure state updates properly
    setTimeout(() => {
      const newConfig = { ...config, isConfigured: true };
      try {
        localStorage.setItem('umamiConfig', JSON.stringify(newConfig));
        setConfig(newConfig);
        setIsEditing(false);
        
        // Add the script to the page
        addUmamiScript(newConfig);
        
        toast({
          title: "Analytics configured",
          description: "Umami Analytics has been set up successfully"
        });
      } catch (error) {
        console.error('Error saving Umami config:', error);
        toast({
          title: "Configuration error",
          description: "Failed to save analytics configuration",
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    }, 300);
  };

  const addUmamiScript = (config: UmamiConfig) => {
    // Remove any existing script to avoid duplicates
    const existingScript = document.getElementById('umami-script');
    if (existingScript) {
      existingScript.remove();
    }

    // Add new script
    const script = document.createElement('script');
    script.id = 'umami-script';
    script.async = true;
    script.defer = true;
    script.setAttribute('data-website-id', config.websiteId);
    script.src = config.scriptUrl;
    document.head.appendChild(script);
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset the analytics configuration?')) {
      try {
        localStorage.removeItem('umamiConfig');
        const existingScript = document.getElementById('umami-script');
        if (existingScript) {
          existingScript.remove();
        }
        
        setConfig({ scriptUrl: '', websiteId: '', isConfigured: false });
        setIsEditing(true);
        
        toast({
          title: "Configuration reset",
          description: "Analytics configuration has been reset"
        });
      } catch (error) {
        console.error('Error resetting Umami config:', error);
      }
    }
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    setTimeout(() => {
      setIsRefreshing(false);
      toast({
        title: "Dashboard refreshed",
        description: "Analytics dashboard has been refreshed"
      });
    }, 1000);
  };

  const getDashboardUrl = () => {
    if (!config.scriptUrl) return '';
    // Remove /umami.js or /script.js from the URL to get the base URL
    return config.scriptUrl.replace(/\/(umami|script)\.js$/, '');
  };

  const getEmbedUrl = () => {
    // If we have a direct share URL, use that instead
    if (config.directShareUrl) {
      return config.directShareUrl;
    }
    
    // Otherwise, construct the URL from the script URL and website ID
    const baseUrl = getDashboardUrl();
    if (!baseUrl || !config.websiteId) return '';
    return `${baseUrl}/share/${config.websiteId}`;
  };

  // Add script on initial render if configured
  useEffect(() => {
    if (config.isConfigured) {
      addUmamiScript(config);
    }
  }, []);

  // Handle iframe load error
  const handleIframeError = () => {
    console.log("Iframe loading failed - CSP restriction");
    setIframeError(true);
  };

  // Handle iframe load success
  const handleIframeLoad = () => {
    setIframeError(false);
  };

  // Responsive embedded dashboard using Dialog (desktop) or Drawer (mobile)
  const EmbeddedDashboard = () => {
    if (isDesktop) {
      return (
        <Dialog open={openEmbeddedDashboard} onOpenChange={setOpenEmbeddedDashboard}>
          <DialogTrigger asChild>
            <Button variant="default" className="gap-2">
              Open Embedded Dashboard
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[90vw] sm:max-h-[90vh] h-[80vh]">
            <DialogHeader>
              <DialogTitle>Umami Analytics Dashboard</DialogTitle>
              <DialogDescription>
                Viewing shared analytics for your website
              </DialogDescription>
            </DialogHeader>
            <div className="h-full w-full min-h-[60vh]">
              {iframeError && (
                <Alert variant="destructive" className="mb-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Embedding Blocked</AlertTitle>
                  <AlertDescription>
                    Direct embedding is blocked due to Content Security Policy restrictions.
                    Please use the link below to open the dashboard in a new tab.
                  </AlertDescription>
                </Alert>
              )}
              <div className="relative h-full">
                <iframe 
                  src={getEmbedUrl()}
                  className={`w-full h-full border-0 ${iframeError ? 'hidden' : 'block'}`}
                  onError={handleIframeError}
                  onLoad={handleIframeLoad}
                />
                {iframeError && (
                  <div className="flex justify-center mt-4">
                    <Button
                      variant="default"
                      className="gap-2"
                      onClick={() => {
                        window.open(getEmbedUrl(), '_blank');
                      }}
                    >
                      Open Dashboard in New Tab
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      );
    }
    
    return (
      <Drawer open={openEmbeddedDashboard} onOpenChange={setOpenEmbeddedDashboard}>
        <DrawerTrigger asChild>
          <Button variant="default" className="gap-2">
            Open Embedded Dashboard
          </Button>
        </DrawerTrigger>
        <DrawerContent className="h-[80vh]">
          <DrawerHeader>
            <DrawerTitle>Umami Analytics Dashboard</DrawerTitle>
            <DrawerDescription>
              Viewing shared analytics for your website
            </DrawerDescription>
          </DrawerHeader>
          <div className="px-4 h-full pb-8">
            {iframeError && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Embedding Blocked</AlertTitle>
                <AlertDescription>
                  Direct embedding is blocked due to Content Security Policy restrictions.
                  Please use the link below to open the dashboard in a new tab.
                </AlertDescription>
              </Alert>
            )}
            <div className="relative h-full">
              <iframe 
                src={getEmbedUrl()}
                className={`w-full h-full border-0 ${iframeError ? 'hidden' : 'block'}`}
                onError={handleIframeError}
                onLoad={handleIframeLoad}
              />
              {iframeError && (
                <div className="flex justify-center mt-4">
                  <Button
                    variant="default"
                    className="gap-2"
                    onClick={() => {
                      window.open(getEmbedUrl(), '_blank');
                    }}
                  >
                    Open Dashboard in New Tab
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>
          <DrawerFooter>
            <DrawerClose asChild>
              <Button variant="outline">Close</Button>
            </DrawerClose>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  };

  if (!config.isConfigured && !isEditing) {
    return (
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Configure Umami Analytics</CardTitle>
          <CardDescription>
            Set up your Umami Analytics to start tracking visitor data
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => setIsEditing(true)}>
            Configure Analytics
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-6">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Umami Analytics</CardTitle>
          <CardDescription>
            {config.isConfigured 
              ? 'View your website analytics data' 
              : 'Configure your Umami Analytics integration'}
          </CardDescription>
        </div>
        {config.isConfigured && !isEditing && (
          <Button variant="outline" onClick={() => setIsEditing(true)}>
            Edit Configuration
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {isEditing ? (
          <div className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="scriptUrl">Umami Script URL</Label>
              <Input 
                id="scriptUrl"
                placeholder="https://analytics.example.com/umami.js"
                value={config.scriptUrl}
                onChange={(e) => setConfig({...config, scriptUrl: e.target.value})}
              />
              <p className="text-sm text-muted-foreground">
                The URL where your Umami script is hosted
              </p>
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="websiteId">Website ID</Label>
              <Input 
                id="websiteId"
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                value={config.websiteId}
                onChange={(e) => setConfig({...config, websiteId: e.target.value})}
              />
              <p className="text-sm text-muted-foreground">
                The unique identifier for your website in Umami
              </p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="directShareUrl">Direct Share URL (Optional)</Label>
              <Input 
                id="directShareUrl"
                placeholder="https://cloud.umami.is/share/KmVfwgxYdaTMI9xa/founder.bitroot.org"
                value={config.directShareUrl || ''}
                onChange={(e) => setConfig({...config, directShareUrl: e.target.value})}
              />
              <p className="text-sm text-muted-foreground">
                A direct URL to your shared dashboard (if available)
              </p>
            </div>
            
            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={isLoading}>
                {isLoading ? "Saving..." : "Save Configuration"}
              </Button>
              {config.isConfigured && (
                <Button variant="outline" onClick={() => setIsEditing(false)}>
                  Cancel
                </Button>
              )}
            </div>
            
            <div className="mt-4 p-4 bg-muted rounded-md">
              <h3 className="font-semibold mb-2">How to get started with Umami:</h3>
              <ol className="list-decimal list-inside space-y-1 text-sm">
                <li>Self-host Umami or use a free hosting service</li>
                <li>Create a new website in your Umami dashboard</li>
                <li>Copy the Website ID from your dashboard</li>
                <li>Enter the script URL and Website ID above</li>
                <li>Or use a direct share URL if you have one</li>
              </ol>
              <p className="text-sm mt-2">
                <a href="https://umami.is/docs/install" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  Learn more about setting up Umami →
                </a>
              </p>
            </div>
          </div>
        ) : (
          config.isConfigured && (
            <div className="space-y-4">
              <Tabs defaultValue="dashboard">
                <TabsList>
                  <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
                  <TabsTrigger value="embedded">Embedded View</TabsTrigger>
                  <TabsTrigger value="info">Connection Info</TabsTrigger>
                </TabsList>
                
                <TabsContent value="dashboard" className="space-y-4">
                  <div className="rounded-md border p-4 bg-muted">
                    <p>To view your full Umami dashboard:</p>
                    <Button 
                      variant="outline" 
                      className="mt-2 gap-2" 
                      onClick={() => {
                        window.open(getEmbedUrl(), '_blank');
                      }}
                    >
                      Open Umami Dashboard
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  <div className="border rounded-md flex items-center justify-center flex-col p-4">
                    <p className="text-muted-foreground text-center mb-4">
                      Your analytics data is being collected. Visit your Umami dashboard to view detailed statistics.
                    </p>
                    <div className="flex gap-2 mt-2">
                      <Button 
                        variant="secondary" 
                        onClick={() => {
                          window.open(getEmbedUrl(), '_blank');
                        }}
                      >
                        View Dashboard
                      </Button>
                      <Button variant="destructive" onClick={handleReset}>
                        Reset Configuration
                      </Button>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="embedded" className="space-y-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium">Analytics Dashboard</h3>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleRefresh}
                      disabled={isRefreshing}
                      className="gap-2"
                    >
                      <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                  </div>

                  {getEmbedUrl() ? (
                    <div className="space-y-4">
                      <Alert variant="warning" className="mb-4">
                        <AlertCircle className="h-4 w-4" />
                        <AlertTitle>Embedding May Be Blocked</AlertTitle>
                        <AlertDescription>
                          Umami's Content Security Policy may prevent direct embedding in iframes. 
                          We'll try to load it below, but if it doesn't appear, use the external link.
                        </AlertDescription>
                      </Alert>
                      
                      <div className="border rounded-md p-4 bg-background">
                        <div className="aspect-video w-full">
                          <iframe 
                            src={getEmbedUrl()}
                            className="w-full h-full border-0"
                            onError={handleIframeError}
                            onLoad={handleIframeLoad}
                          />
                        </div>
                        {iframeError && (
                          <div className="p-4 flex flex-col items-center">
                            <p className="text-center mb-4">Embedding blocked by Umami's security policy.</p>
                            <Button
                              variant="default"
                              className="gap-2"
                              onClick={() => {
                                window.open(getEmbedUrl(), '_blank');
                              }}
                            >
                              Open Dashboard in New Tab
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex justify-center">
                        <EmbeddedDashboard />
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 border rounded-md bg-muted">
                      <p>Could not generate dashboard URL. Please check your configuration.</p>
                    </div>
                  )}
                </TabsContent>
                
                <TabsContent value="info">
                  <div className="space-y-4">
                    <div>
                      <Label className="text-muted-foreground">Script URL</Label>
                      <p className="font-mono text-sm break-all border p-2 rounded-md mt-1">
                        {config.scriptUrl}
                      </p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Website ID</Label>
                      <p className="font-mono text-sm break-all border p-2 rounded-md mt-1">
                        {config.websiteId}
                      </p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Direct Share URL</Label>
                      <p className="font-mono text-sm break-all border p-2 rounded-md mt-1">
                        {config.directShareUrl || 'Not set'}
                      </p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Embedded Share URL</Label>
                      <p className="font-mono text-sm break-all border p-2 rounded-md mt-1">
                        {getEmbedUrl() || 'Not available'}
                      </p>
                    </div>
                    <Button variant="destructive" size="sm" onClick={handleReset} className="mt-4">
                      Reset Configuration
                    </Button>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          )
        )}
      </CardContent>
    </Card>
  );
};

export default UmamiAnalytics;
